import { useCallback, useEffect, useState } from "react";
import styles from "./EntrepreneurRelatorio.module.css";
import { useTranslation } from "react-i18next";
import api from "../../utils/axios/api";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

interface ReportProductRow {
    id: string;
    name: string;
    storeName: string;
    stock: number;
    unitsSold: number;
    grossSales: number;
    platformFees: number;
    netSales: number;
}

interface ReportSummary {
    paidOrdersWithFee: number;
    grossOrderTotal: number;
    totalPlatformFees: number;
    netAfterFees: number;
}

interface ReportProducts {
    page: number;
    pages: number;
    totalProducts: number;
    items: ReportProductRow[];
}

interface ReportResponse {
    summary: ReportSummary;
    products: ReportProducts;
}

const money = (n: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

export default function Relatorios() {
    const [report, setReport] = useState<ReportResponse | null>(null);
    const [selectedPage, setSelectedPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation("translation", { keyPrefix: "entrepeneurRelatorio" });

    const fetchReport = useCallback(async (page: number) => {
        const entrepeneurId = localStorage.getItem("entrepeneurId");
        if (!entrepeneurId) {
            setReport(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await api.get<ReportResponse>(`/entrepeneur/report/${entrepeneurId}`, {
                params: { page },
            });
            setReport(response.data);
            setSelectedPage(response.data.products.page);
        } catch {
            console.error("Erro ao buscar relatório");
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReport(1);
    }, [fetchReport]);

    const pages = report?.products.pages ?? 1;

    const goPage = (p: number) => {
        if (p < 1 || p > pages) return;
        fetchReport(p);
    };

    return (
        <main className={styles.mainContainer}>
            <section className={styles.wrapper}>
                <div className={styles.tituloContainer}>
                    <p className={styles.titulo}>{t("logs")}</p>
                </div>

                {report && (
                    <div className={styles.summaryBox}>
                        <p className={styles.summaryTitle}>{t("summaryTitle")}</p>
                        <ul className={styles.summaryList}>
                            <li>
                                {t("paidOrdersWithFee")} <strong>{report.summary.paidOrdersWithFee}</strong>
                            </li>
                            <li>
                                {t("grossOrderTotal")}{" "}
                                <strong>{money(report.summary.grossOrderTotal)}</strong>
                            </li>
                            <li>
                                {t("totalPlatformFees")}{" "}
                                <strong>{money(report.summary.totalPlatformFees)}</strong>
                            </li>
                            <li>
                                {t("netAfterFees")} <strong>{money(report.summary.netAfterFees)}</strong>
                            </li>
                        </ul>
                    </div>
                )}

                <h2 className={styles.subtitulo}>{t("productTableTitle")}</h2>

                {loading && <p className={styles.texto}>{t("loading")}</p>}

                {!loading && report && report.products.items.length === 0 && (
                    <p className={styles.texto}>{t("noProducts")}</p>
                )}

                {!loading && report && report.products.items.length > 0 && (
                    <>
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t("colProduct")}</th>
                                        <th>{t("colStore")}</th>
                                        <th>{t("colStock")}</th>
                                        <th>{t("colUnitsSold")}</th>
                                        <th>{t("colGross")}</th>
                                        <th>{t("colFees")}</th>
                                        <th>{t("colNet")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.products.items.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.name}</td>
                                            <td>{row.storeName}</td>
                                            <td>{row.stock}</td>
                                            <td>{row.unitsSold}</td>
                                            <td>{money(row.grossSales)}</td>
                                            <td>{money(row.platformFees)}</td>
                                            <td>{money(row.netSales)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className={styles.pageInfo}>
                            {t("pageInfo", {
                                page: report.products.page,
                                pages: report.products.pages,
                                total: report.products.totalProducts,
                            })}
                        </p>
                        <div className={styles.pages}>
                            {selectedPage > 1 && (
                                <IoIosArrowBack
                                    className={styles.paginationButton}
                                    role="button"
                                    aria-label={t("prevPage")}
                                    onClick={() => goPage(selectedPage - 1)}
                                />
                            )}
                            {Array.from({ length: pages }, (_, i) => i + 1)
                                .filter((item) => {
                                    if (pages <= 7) return true;
                                    if (item <= 6 || item === pages) return true;
                                    if (item === selectedPage) return true;
                                    return false;
                                })
                                .map((item, key, filteredArray) => {
                                    const isEllipsis =
                                        filteredArray[key + 1] && filteredArray[key + 1] !== item + 1;
                                    return (
                                        <div key={key}>
                                            <button
                                                type="button"
                                                onClick={() => goPage(item)}
                                                className={item === selectedPage ? styles.selectedPage : ""}
                                            >
                                                {item}
                                            </button>
                                            {isEllipsis && <span className={styles.elipsis}>...</span>}
                                        </div>
                                    );
                                })}
                            {selectedPage < pages && (
                                <IoIosArrowForward
                                    className={styles.paginationButton}
                                    role="button"
                                    aria-label={t("nextPage")}
                                    onClick={() => goPage(selectedPage + 1)}
                                />
                            )}
                        </div>
                    </>
                )}
            </section>
        </main>
    );
}
